var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { hasValue, textSearch } from "../../../../utils";
import classNames from "classnames";
import SearchBox from "../../forms/inputs/SearchBox";
import TableComponent from "../Tables/TableComponent";
import useDebounce from "../../../../hooks/useDebounce";
import Button from "@widget/buttons/Button";
import Modal from "../../popups/Modal";
import DynamicForm from "../../forms/DynamicForm/DynamicForm";
import { cloneDeep } from 'lodash-es';
import useToast from "@notifications/Toast/useToast";
import useAlert from "@notifications/Alerts/useAlerts";
import './CRUDComponent.scss';
;
// Helper functions for validation
const validateAddView = (disabled, add, renderCustomAddView) => {
    if (disabled)
        return false;
    return !!(((add === null || add === void 0 ? void 0 : add.onSubmit) &&
        typeof add.onSubmit === 'function' &&
        Array.isArray(add.formStructure) &&
        add.formStructure.length > 0 &&
        add.formStructure.some(section => Array.isArray(section.fields) && section.fields.length > 0)) || renderCustomAddView);
};
const validateEditView = (disabled, edit, renderCustomEditView) => {
    if (disabled)
        return false;
    return !!(((edit === null || edit === void 0 ? void 0 : edit.onSubmit) &&
        typeof edit.onSubmit === 'function' &&
        Array.isArray(edit.formStructure) &&
        edit.formStructure.length > 0 &&
        edit.formStructure.some(section => Array.isArray(section.fields) && section.fields.length > 0)) || renderCustomEditView);
};
const validateDeleteAction = (disabled, onDeleteItem) => {
    return !disabled && typeof onDeleteItem === 'function';
};
const CRUDComponentBase = React.forwardRef((props, ref) => {
    const { list, add, edit, renderCustomAddView, renderCustomEditView, disableViews, entityName, className } = props;
    const { title, search, addButton, renderFilters } = list;
    const [searchText, setSearchtext] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [editInstance, setEditInstance] = useState(null);
    const query = useDebounce(searchText, 500);
    const listRef = useRef(null);
    const refreshList = useCallback(() => {
        var _a;
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.refreshList();
    }, []);
    useImperativeHandle(ref, () => ({
        refreshList
    }), [refreshList]);
    const entity = useMemo(() => hasValue(entityName) ? entityName : 'Record', [entityName]);
    const { canShowAdd, canShowEdit, canShowDelete } = useMemo(() => ({
        canShowAdd: validateAddView(disableViews === null || disableViews === void 0 ? void 0 : disableViews.add, add, renderCustomAddView),
        canShowEdit: validateEditView(disableViews === null || disableViews === void 0 ? void 0 : disableViews.edit, edit, renderCustomEditView),
        canShowDelete: validateDeleteAction(disableViews === null || disableViews === void 0 ? void 0 : disableViews.delete, list === null || list === void 0 ? void 0 : list.onDeleteItem)
    }), [disableViews, add, edit, renderCustomAddView, renderCustomEditView, list === null || list === void 0 ? void 0 : list.onDeleteItem]);
    const onCloseForm = useCallback(() => {
        setShowAdd(false);
        setEditInstance(null);
    }, []);
    const afterSave = useCallback(() => {
        refreshList();
        onCloseForm();
    }, [refreshList, onCloseForm]);
    useEffect(() => {
        console.log('show_add_', showAdd);
    }, [showAdd]);
    const addForm = useMemo(() => {
        if (!canShowAdd)
            return null;
        if (renderCustomAddView)
            return renderCustomAddView(showAdd, onCloseForm);
        return (_jsx(FormView, { show: showAdd, onClose: onCloseForm, mode: "add", onSave: afterSave, entityName: entity, formProps: add }));
    }, [canShowAdd, showAdd, onCloseForm, afterSave, entity, add, renderCustomAddView]);
    const editForm = useMemo(() => {
        if (!canShowEdit)
            return null;
        if (renderCustomEditView)
            return renderCustomEditView(!!editInstance, onCloseForm, editInstance);
        return (_jsx(FormView, { show: !!editInstance, onClose: onCloseForm, mode: "edit", onSave: afterSave, entityName: entity, editInstance: editInstance, formProps: edit }));
    }, [canShowEdit, editInstance, onCloseForm, afterSave, entity, edit, renderCustomEditView]);
    const headerRight = useMemo(() => {
        var _a, _b;
        return (_jsxs("div", { className: "uxpcore_crud__right-container", children: [renderFilters && _jsx("div", { className: "uxpcore_crud__filter-container", children: renderFilters }), (search === null || search === void 0 ? void 0 : search.enabled) && (_jsx("div", { className: "uxpcore_crud__search", children: _jsx(SearchBox, { value: searchText, onChange: setSearchtext, placeholder: "Search..." }) })), canShowAdd && (_jsx(Button, { title: hasValue(addButton === null || addButton === void 0 ? void 0 : addButton.label) ? addButton.label : 'Add Item', icon: hasValue(addButton === null || addButton === void 0 ? void 0 : addButton.icon) ? addButton.icon : 'fas plus', onClick: () => setShowAdd(true), styles: {
                        backgroundColor: (_a = addButton === null || addButton === void 0 ? void 0 : addButton.backgroundColor) !== null && _a !== void 0 ? _a : null,
                        color: (_b = addButton === null || addButton === void 0 ? void 0 : addButton.color) !== null && _b !== void 0 ? _b : null,
                    } }))] }));
    }, [renderFilters, search === null || search === void 0 ? void 0 : search.enabled, searchText, canShowAdd, addButton]);
    return (_jsxs("div", { className: classNames('uxpcore_crud__container', className), children: [_jsxs("div", { className: "uxpcore_crud__header", children: [_jsx("div", { className: "uxpcore_crud__left-container", children: _jsx("div", { className: "uxpcore_crud__title", children: title }) }), headerRight] }), _jsxs("div", { className: "uxpcore_crud__body", children: [_jsx(ListView, { ref: listRef, list: list, query: query || '', entityName: entity, onClickEdit: setEditInstance, disableViews: {
                            add: !canShowAdd,
                            edit: !canShowEdit,
                            delete: !canShowDelete
                        } }), addForm, editForm] })] }));
});
const ListView = React.forwardRef((props, ref) => {
    const { list, query, disableViews, onClickEdit, entityName } = props;
    const { columns, defaultPageSize, data: { isPaginated, getData, getTotal, isLoading }, search, noItemsMessage, onDeleteItem, filters, onChangeFilters, minCellWidth, onClickRow, onClickColumn } = list;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(isLoading);
    const [counter, setCounter] = useState(0);
    const toast = useToast();
    const alerts = useAlert();
    useImperativeHandle(ref, () => ({
        refreshList: () => setCounter(prev => prev + 1)
    }), []);
    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (!isPaginated || Array.isArray(getData)) {
                setLoading(true);
                if (Array.isArray(getData))
                    setData(getData);
                else if (!isPaginated && typeof getData === 'function') {
                    try {
                        const { items } = yield getData();
                        setData(items || []);
                    }
                    catch (e) {
                        toast.error('Unable to load data. Something went wrong.');
                        console.error('Unable to get data. Error: ', e);
                    }
                }
                setLoading(false);
            }
        }))();
    }, [isPaginated, getData, counter]);
    const filterByQuery = useCallback((items) => {
        var _a;
        if (!(search === null || search === void 0 ? void 0 : search.enabled) || !hasValue(query))
            return items;
        const fields = ((_a = search === null || search === void 0 ? void 0 : search.fields) === null || _a === void 0 ? void 0 : _a.length) ? search.fields : columns.map(c => c.id);
        return items.filter(d => fields.some(f => textSearch(d[f], query, true)));
    }, [query, search, columns]);
    const filterData = useCallback(() => {
        const filtered = onChangeFilters ? onChangeFilters([...data]) : [...data];
        return filterByQuery(filtered);
    }, [data, onChangeFilters, filterByQuery]);
    const calculateTotal = useCallback(() => {
        return filterData().length;
    }, [filterData]);
    const getTotalCount = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (isPaginated) {
                if (typeof getTotal !== 'function') {
                    toast.error('getTotal function is required');
                    console.error('getTotal function is required for pagination');
                    return 0;
                }
                return (yield getTotal(query, filters)) || 0;
            }
            return calculateTotal();
        }
        catch (e) {
            toast.error('Unable to get total. Something went wrong.');
            console.error('Unable to get total. Error: ', e);
            return 0;
        }
    }), [isPaginated, getTotal, query, filters, calculateTotal, counter]);
    const getPaginatedData = useCallback((page, pageSize) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (isPaginated && typeof getData === 'function') {
                return getData(page, pageSize, query, filters);
            }
            const paginated = filterData().slice((page - 1) * pageSize, page * pageSize);
            return { items: paginated };
        }
        catch (e) {
            toast.error('Unable to get data. Something went wrong.');
            console.error('Unable to get data. Error: ', e);
            return { items: [] };
        }
    }), [isPaginated, getData, query, filters, filterData, counter]);
    const handleDelete = useCallback((item) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const confirmed = yield alerts.confirm({
                title: 'Are you sure?',
                content: `You are about to delete a ${entityName === null || entityName === void 0 ? void 0 : entityName.toLowerCase()}. This cannot be undone. Do you wish to continue?`
            });
            if (!confirmed)
                return;
            const { status, message } = yield onDeleteItem(item);
            const toastMessage = message || (status === 'error'
                ? `Unable to delete ${entityName.toLowerCase()}. Something went wrong`
                : `${entityName} deleted!`);
            status === 'error' ? alerts.error(toastMessage) : toast.success(toastMessage);
            setCounter(prev => prev + 1);
        }
        catch (e) {
            console.error('Unable to delete item. Error: ', e);
            alerts.error(`Unable to delete ${entityName.toLowerCase()}. Something went wrong`);
        }
    }), [alerts, entityName, onDeleteItem]);
    return (_jsx(TableComponent, { columns: columns, pageSize: defaultPageSize || 10, total: getTotalCount, data: getPaginatedData, loading: loading, noItemsMessage: noItemsMessage, editColumn: {
            enable: !(disableViews === null || disableViews === void 0 ? void 0 : disableViews.edit),
            onEdit: onClickEdit,
        }, deleteColumn: {
            enable: !(disableViews === null || disableViews === void 0 ? void 0 : disableViews.delete),
            onDelete: handleDelete,
        }, minCellWidth: minCellWidth, onClickRow: onClickRow, onClickColumn: onClickColumn }));
});
const FormView = React.forwardRef((props, ref) => {
    const { show, onClose, formProps, onSave, entityName, mode, editInstance, } = props;
    const toast = useToast();
    const alerts = useAlert();
    function handleSubmit(data) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const isEditMode = mode === 'edit';
            try {
                const onSubmit = isEditMode
                    ? (formProps === null || formProps === void 0 ? void 0 : formProps.onSubmit)(data, editInstance)
                    : (formProps === null || formProps === void 0 ? void 0 : formProps.onSubmit)(data);
                const { status, data: record, message } = yield onSubmit;
                if (status === 'error') {
                    alerts.error(message || `Unable to ${isEditMode ? 'update' : 'save'} the ${entityName === null || entityName === void 0 ? void 0 : entityName.toLowerCase()}. Something went wrong`);
                }
                else if (status == 'no-change') {
                    toast.info(message || 'No changes detected');
                }
                else {
                    (_a = formProps === null || formProps === void 0 ? void 0 : formProps.afterSave) === null || _a === void 0 ? void 0 : _a.call(formProps, record || null);
                    onSave(record || null);
                    toast.success(message || `${entityName} ${isEditMode ? 'updated' : 'saved'}!`);
                }
            }
            catch (e) {
                console.error(`Unable to ${isEditMode ? 'update' : 'save'} the item. Error: `, e);
                alerts.error(`Unable to ${isEditMode ? 'update' : 'save'} the ${entityName === null || entityName === void 0 ? void 0 : entityName.toLowerCase()}. Something went wrong`);
            }
        });
    }
    function handleCancel() {
        var _a;
        (_a = formProps === null || formProps === void 0 ? void 0 : formProps.onCancel) === null || _a === void 0 ? void 0 : _a.call(formProps);
        onClose();
    }
    function getFormStructure() {
        if (mode === 'edit' && editInstance) {
            return ((formProps === null || formProps === void 0 ? void 0 : formProps.formStructure) || []).map(section => (Object.assign(Object.assign({}, section), { fields: section.fields.map((field) => (Object.assign(Object.assign({}, field), { value: cloneDeep(editInstance[field.name]) || field.value || '' }))) })));
        }
        return cloneDeep((formProps === null || formProps === void 0 ? void 0 : formProps.formStructure) || []); // Deep clone default structure
    }
    const maxNumOfColumns = ((formProps === null || formProps === void 0 ? void 0 : formProps.formStructure) || []).reduce((a, b) => (Math.max(a, (b === null || b === void 0 ? void 0 : b.columns) || 1)), 1);
    const defaultStyles = { width: maxNumOfColumns == 1 ? '450px' : maxNumOfColumns == 2 ? '700px' : 'auto' };
    if (!formProps)
        return null;
    return (_jsx(Modal, { show: show, onClose: handleCancel, className: 'uxpcore_crud__addview-container', title: (formProps === null || formProps === void 0 ? void 0 : formProps.title) || (mode === 'edit' ? 'Edit Item' : 'Add Item'), autoSize: true, children: _jsx(DynamicForm, { formStructure: getFormStructure(), onSubmit: handleSubmit, onCancel: handleCancel, submitButtonLabel: formProps === null || formProps === void 0 ? void 0 : formProps.submitButtonLabel, submitButtonLoadingLabel: formProps === null || formProps === void 0 ? void 0 : formProps.submitButtonLoadingLabel, hideCancelButton: (formProps === null || formProps === void 0 ? void 0 : formProps.hideCancelButton) || false, cancelButtonLabel: formProps === null || formProps === void 0 ? void 0 : formProps.cancelButtonLabel, formContainerStyles: (formProps === null || formProps === void 0 ? void 0 : formProps.formContainerStyles) || defaultStyles }) }));
});
/**
 * @export
 * @deprecated Use SimpleConfigurationTable instead. CRUDComponent will be removed in a future release.
 *
 * CRUD component
 */
const CRUDComponent = React.memo(CRUDComponentBase);
export default React.memo(CRUDComponent);
